/** Browser mirror of src/shared/workflowLayout.ts. The contract test pins the body byte-for-byte. */

export interface WorkflowLayoutNode {
  id: string;
  deps: readonly string[];
  status?: string;
  startedAt?: number;
}
export interface WorkflowGeometry {
  cardWidth: number;
  cardHeight(node: WorkflowLayoutNode): number;
  columnGap: number;
  rowGap: number;
  viaHeight: number;
  laneInset: number;
  laneSpacing: number;
  laneCount: number;
  padding: number;
}
export interface WorkflowPlacement {
  id: string;
  column: number;
  row: number;
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface WorkflowEdge {
  from: string;
  to: string;
  points: { x: number; y: number }[];
}
export interface WorkflowLayout {
  nodes: WorkflowPlacement[];
  edges: WorkflowEdge[];
  width: number;
  height: number;
}
export type WorkflowDirection = 'up' | 'down' | 'left' | 'right';

/** Longest-path layering, stable barycenter ordering and total placement of dangling/cyclic input. */
export function layoutWaves<T extends WorkflowLayoutNode>(nodes: readonly T[]): T[][] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const level = new Map<string, number>();
  const visiting = new Set<string>();
  const waveOf = (id: string): number => {
    const known = level.get(id);
    if (known !== undefined) return known;
    const node = byId.get(id);
    if (!node || visiting.has(id)) return -1;
    visiting.add(id);
    const wave = node.deps.reduce((deepest, dep) => dep === id ? deepest : Math.max(deepest, waveOf(dep) + 1), 0);
    visiting.delete(id);
    level.set(id, wave);
    return wave;
  };
  for (const node of nodes) waveOf(node.id);
  const columns: T[][] = [];
  for (const node of nodes) (columns[level.get(node.id) ?? 0] ??= []).push(node);
  const dense = columns.filter((column) => column.length > 0);
  const rank = new Map<string, number>();
  dense[0]?.forEach((node, index) => rank.set(node.id, index));
  for (let column = 1; column < dense.length; column += 1) {
    const barycenter = (node: T): number => {
      const ranks = node.deps.map((dep) => rank.get(dep)).filter((value): value is number => value !== undefined);
      return ranks.length ? ranks.reduce((sum, value) => sum + value, 0) / ranks.length : Number.MAX_SAFE_INTEGER;
    };
    dense[column] = [...dense[column]!].sort((a, b) => barycenter(a) - barycenter(b));
    dense[column]!.forEach((node, index) => rank.set(node.id, index));
  }
  return dense;
}

interface Slot {
  id: string;
  via?: { from: string; to: string };
  node?: WorkflowLayoutNode;
  rank: number;
}

/** Add a via slot in every wave skipped by a dependency, ordered by the source's barycenter. */
function withViaSlots(waves: readonly WorkflowLayoutNode[][]): Slot[][] {
  const columnOf = new Map(waves.flatMap((wave, column) => wave.map((node) => [node.id, column] as const)));
  const slots: Slot[][] = waves.map((wave) => wave.map((node, rank) => ({ id: node.id, node, rank })));
  for (const [targetColumn, wave] of waves.entries()) {
    for (const target of wave) {
      for (const from of target.deps) {
        const sourceColumn = columnOf.get(from);
        if (sourceColumn === undefined || sourceColumn >= targetColumn - 1) continue;
        const source = slots[sourceColumn]!.find((slot) => slot.id === from)!;
        for (let column = sourceColumn + 1; column < targetColumn; column += 1) {
          const id = `${from}->${target.id}@${column}`;
          const rank = column === sourceColumn + 1 ? source.rank : slots[column - 1]!.find((slot) => slot.id === `${from}->${target.id}@${column - 1}`)!.rank;
          slots[column]!.push({ id, via: { from, to: target.id }, rank });
        }
      }
    }
  }
  // Keep the original stable card order and insert slots at the nearest source row.
  for (const column of slots) column.sort((a, b) => a.rank - b.rank);
  return slots;
}

/** Geometry is provided by the client: pixel cards for web, cells for CLI. No client reimplements layering or routing. */
function placeColumns(waves: readonly WorkflowLayoutNode[][], geometry: WorkflowGeometry): WorkflowLayout {
  if (waves.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };
  const columns = withViaSlots(waves);
  const slotHeight = (slot: Slot): number => slot.node ? geometry.cardHeight(slot.node) : geometry.viaHeight;
  const heightOf = (column: Slot[]): number =>
    column.reduce((sum, slot) => sum + slotHeight(slot), 0) + Math.max(0, column.length - 1) * geometry.rowGap;
  const tallest = Math.max(...columns.map(heightOf));
  const positioned = new Map<string, WorkflowPlacement>();
  const nodes: WorkflowPlacement[] = [];
  for (const [column, slots] of columns.entries()) {
    let y = geometry.padding + Math.round((tallest - heightOf(slots)) / 2);
    for (const [row, slot] of slots.entries()) {
      const placement = {
        id: slot.id, column, row,
        x: geometry.padding + column * (geometry.cardWidth + geometry.columnGap),
        y, width: geometry.cardWidth, height: slotHeight(slot),
      };
      positioned.set(slot.id, placement);
      if (slot.node) nodes.push(placement);
      y += placement.height + geometry.rowGap;
    }
  }
  return {
    nodes,
    edges: routeEdges(waves, positioned, geometry),
    width: geometry.padding * 2 + waves.length * geometry.cardWidth + (waves.length - 1) * geometry.columnGap,
    height: geometry.padding * 2 + tallest,
  };
}

/** Orthogonal point routes, split at each via slot; no horizontal segment enters another card's wave. */
function routeEdges(
  waves: readonly WorkflowLayoutNode[][],
  positioned: ReadonlyMap<string, WorkflowPlacement>,
  geometry: WorkflowGeometry,
): WorkflowEdge[] {
  const edges: WorkflowEdge[] = [];
  const lanes = new Map<number, number>();
  for (const wave of waves) for (const node of wave) for (const from of node.deps) {
    const source = positioned.get(from);
    const target = positioned.get(node.id);
    if (!source || !target || source.column >= target.column) continue;
    const points: WorkflowEdge['points'] = [{ x: source.x + source.width, y: source.y + source.height / 2 }];
    let previous = source;
    for (let column = source.column + 1; column <= target.column; column += 1) {
      const next = column === target.column ? target : positioned.get(`${from}->${node.id}@${column}`)!;
      const lane = lanes.get(column) ?? 0;
      lanes.set(column, lane + 1);
      const track = next.x - geometry.columnGap + geometry.laneInset
        + (lane % geometry.laneCount) * geometry.laneSpacing;
      const startY = previous.y + previous.height / 2;
      const endY = next.y + next.height / 2;
      points.push({ x: track, y: startY }, { x: track, y: endY }, { x: next.x, y: endY });
      if (column < target.column) points.push({ x: next.x + next.width, y: endY });
      previous = next;
    }
    edges.push({ from, to: node.id, points });
  }
  return edges;
}

export function layoutWorkflow<T extends WorkflowLayoutNode>(nodes: readonly T[], geometry: WorkflowGeometry): WorkflowLayout {
  return placeColumns(layoutWaves(nodes), geometry);
}

/** Arrows step through the visible node positions, clamped at the edge of the graph. */
export function stepSelection(layout: Pick<WorkflowLayout, 'nodes'>, currentId: string | null, direction: WorkflowDirection): string | null {
  const current = layout.nodes.find((node) => node.id === currentId);
  if (!current) return layout.nodes[0]?.id ?? null;
  if (direction === 'up' || direction === 'down') {
    const column = layout.nodes.filter((node) => node.column === current.column);
    const index = column.findIndex((node) => node.id === current.id) + (direction === 'down' ? 1 : -1);
    return column[Math.max(0, Math.min(column.length - 1, index))]?.id ?? current.id;
  }
  const columns = [...new Set(layout.nodes.map((node) => node.column))].sort((a, b) => a - b);
  const index = columns.indexOf(current.column) + (direction === 'right' ? 1 : -1);
  const destination = columns[Math.max(0, Math.min(columns.length - 1, index))];
  if (destination === undefined || destination === current.column) return current.id;
  return layout.nodes.filter((node) => node.column === destination)
    .reduce((best, node) => Math.abs(node.y - current.y) < Math.abs(best.y - current.y) ? node : best).id;
}

export function activeNode<T extends WorkflowLayoutNode>(nodes: readonly T[]): T | undefined {
  let active: T | undefined;
  for (const node of nodes) if (node.status === 'running'
    && (!active || (node.startedAt ?? 0) > (active.startedAt ?? 0))) active = node;
  return active;
}

export function workflowCounts(wf: { nodes: readonly { status: string; tokens?: number }[] }) {
  return {
    done: wf.nodes.filter((node) => node.status === 'done').length,
    running: wf.nodes.filter((node) => node.status === 'running').length,
    error: wf.nodes.filter((node) => node.status === 'error').length,
    pending: wf.nodes.filter((node) => node.status === 'pending').length,
    total: wf.nodes.length,
    tokens: wf.nodes.reduce((sum, node) => sum + (node.tokens ?? 0), 0),
  };
}

export function workflowLabel(wf: { id: string; title?: string; nodes: readonly { task: string }[] }): string {
  return wf.title?.trim() || wf.nodes[0]?.task || wf.id;
}

/** One node's live elapsed seconds; terminal nodes keep their last reported duration. */
export function workflowNodeSeconds(node: { status: string; startedAt?: number; seconds?: number }, now: number): number | undefined {
  return node.status === 'running' && node.startedAt !== undefined
    ? Math.max(node.seconds ?? 0, Math.round((now - node.startedAt) / 1000)) : node.seconds;
}

/** DAG wall-clock elapsed, never a maximum or sum of node durations. Unknown historical timing stays absent. */
export function workflowElapsedSeconds(wf: { status: string; runTiming?: { startedAt: number; finishedAt?: number } }, now: number): number | undefined {
  const timing = wf.runTiming;
  return timing && (wf.status === 'running' || timing.finishedAt !== undefined) ? Math.max(0, Math.round(((timing.finishedAt ?? now) - timing.startedAt) / 1000)) : undefined;
}
